import { DocumentEvidence } from '../models/documentEvidence';
import { DocumentMetadata, MetadataType } from '../models/documentMetadata';

/** Page to display again once the list of annotations to delete is closed. */
export class EvidenceSelectionPageMemory {
  private page: number | null = null;

  get active(): boolean {
    return this.page !== null;
  }

  /** The list has just been opened while the given page was displayed. */
  open(currentPage: number): void {
    this.page = isPageNumber(currentPage) ? currentPage : null;
  }

  /** While the list is open, the user deliberately displayed this page (page arrows, click on a box). */
  anchor(currentPage: number): void {
    if (this.page !== null && isPageNumber(currentPage)) {
      this.page = currentPage;
    }
  }

  /**
   * The list has been closed. Returns the page to display again, or null when the displayed page is already the right
   * one or when the remembered page does not exist in the document.
   */
  close(currentPage: number, pageCount?: number): number | null {
    const page = this.page;
    this.page = null;
    if (page === null || page === currentPage) {
      return null;
    }
    if (pageCount !== undefined && page > pageCount) {
      return null;
    }
    return page;
  }
}

function isPageNumber(page: number): boolean {
  return Number.isInteger(page) && page > 0;
}

export interface EvidenceDeletionResult {
  evidences: DocumentEvidence[];
  metadata: DocumentMetadata[];
  deletedEvidenceIds: Set<string>;
  metadataDeleted: boolean;
}

/**
 * Applies a successful DELETE /documents/:id/metadata/:metadataId to the lists of the document page.
 * Mirrors the backend rule: the selected annotations go, and the proof itself once none of its
 * annotations is left, or when no annotation was selected for a CLASS or LANG proof.
 */
export function removeDeletedEvidences(
  evidences: DocumentEvidence[],
  metadata: DocumentMetadata[],
  deletedMetadata: DocumentMetadata,
  deletedEvidences: DocumentEvidence[],
): EvidenceDeletionResult {
  const deletedEvidenceIds = new Set(
    deletedEvidences
      .map((evidence) => evidence._id)
      .filter((id): id is string => !!id),
  );
  const remainingEvidences = evidences.filter((evidence) => !evidence._id || !deletedEvidenceIds.has(evidence._id));
  const annotationsLeft = remainingEvidences.some((evidence) => evidence.metadata === deletedMetadata._id);
  const metadataDeleted = !annotationsLeft
    || ([MetadataType.CLASS, MetadataType.LANG].includes(deletedMetadata.type) && deletedEvidences.length === 0);

  return {
    evidences: remainingEvidences,
    metadata: metadataDeleted ? metadata.filter((metadatum) => metadatum._id !== deletedMetadata._id) : metadata,
    deletedEvidenceIds,
    metadataDeleted,
  };
}
